const STORAGE_KEY = "boobalyz_assessment_history";
const MAX_ENTRIES = 50;

// Fields that must never be persisted
const PII_FIELDS = ["name", "Name", "nhs", "NHS"];

export interface AssessmentHistoryEntry {
  id: string;
  savedAt: string;
  formData: Record<string, string>;
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function stripPii(formData: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(formData).filter(([k]) => !PII_FIELDS.includes(k)),
  );
}

export function listAssessments(): AssessmentHistoryEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as AssessmentHistoryEntry[];
  } catch {
    return [];
  }
}

export function saveAssessment(
  formData: Record<string, string>,
): AssessmentHistoryEntry {
  const entry: AssessmentHistoryEntry = {
    id: generateId(),
    savedAt: new Date().toISOString(),
    formData: stripPii(formData),
  };

  const existing = listAssessments();
  const updated = [entry, ...existing].slice(0, MAX_ENTRIES);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  return entry;
}

export function deleteAssessment(id: string): void {
  const existing = listAssessments();
  const updated = existing.filter((e) => e.id !== id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
}

export function clearAllAssessments(): void {
  localStorage.removeItem(STORAGE_KEY);
}

export function buildPatientSummary(
  formData: Record<string, string> | null | undefined,
): string {
  if (!formData) return "New Patient";
  const segments: string[] = [];

  // Setting
  const setting = formData["setting"];
  if (setting === "Adjuvant") segments.push("Adjuvant");
  else if (setting === "Non-Adjuvant") segments.push("Non-Adj");
  else if (setting) segments.push(setting);

  // Surgery
  const surgery = formData["surgery"];
  if (surgery === "Breast-Conserving") segments.push("BCS");
  else if (surgery === "Mastectomy") segments.push("Mx");
  else if (surgery) segments.push(surgery);

  // Age
  const age = formData["age"];
  if (age && age !== "0") segments.push(age + "yr");

  // Menopausal status
  const meno = formData["menopausalStatus"];
  if (meno) segments.push(meno);

  return segments.length > 0 ? segments.join(" · ") : "New Patient";
}
